import React, { useState, useEffect } from "react";
import {
  Form,
  Select,
  Checkbox,
  Button,
  Input,
  Row,
  Col,
  Card,
  Space,
  message,
  Tag,
  Typography,
} from "antd";
import { SearchOutlined, PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../api";

const { Option } = Select;
const { Text } = Typography;

const Attendance = () => {
  const [form] = Form.useForm();
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [memberSearchText, setMemberSearchText] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const [preConfirmationRecordId, setPreConfirmationRecordId] = useState(null);
  const [preConfirmedIds, setPreConfirmedIds] = useState([]);

  const [visitors, setVisitors] = useState([]);

  const navigate = useNavigate();


  useEffect(() => {
    fetchEvents();
    fetchMembers();
    fetchVisitors();
  }, []);


  const fetchEvents = async () => {
    try {
      const res = await api.get("/event");
      if (res.data && Array.isArray(res.data.data)) {
        setEvents(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching events:", err);
      message.error("Failed to fetch events.");
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await api.get("/member/get-all-members");
      if (res.data && Array.isArray(res.data.data)) {
        const activeMembers = res.data.data.filter((m) => !m.isDeleted);
        setMembers(activeMembers);
        setFilteredMembers(activeMembers);
      }
    } catch (err) {
      console.error("Error fetching members:", err);
      message.error("Failed to fetch members.");
    }
  };

  const fetchVisitors = async () => {
    try {
      const res = await api.get("/visitor/get-all-visitors");
      if (res.data && Array.isArray(res.data.data)) {
        setVisitors(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching visitors:", err);
    }
  };


  // Load actual attendance first
  const fetchEventAttendance = async (eventId) => {
    try {
      const res = await api.get("/member/geteventattendance");
      if (Array.isArray(res.data)) {
        const records = res.data
          .filter((r) => (r.eventid?._id === eventId || r.eventid === eventId))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        const record = records[0];

        if (record) {
          // Actual attendance exists → load it
          setCurrentRecordId(record._id);
          const loadedMemberIds = (record.memberid || [])
            .map((m) => m._id || m)
            .filter((id) => members.some((mem) => mem._id === id));

          setSelectedMembers(loadedMemberIds);
          form.setFieldsValue({
            eventId: record.eventid._id || record.eventid,
            nonmember: record.nonmember || [],
          });
          message.info("Loaded existing attendance.");
          return true; // Found actual attendance
        }
      }
    } catch (err) {
      console.error("Error fetching attendance:", err);
    }

    // No actual attendance found
    setCurrentRecordId(null);
    return false;
  };

  // Load pre-confirmation data for indicators
  const fetchPreConfirmationData = async (eventId) => {
    try {
      const res = await api.get("/member/geteventpreconfirm");
      if (Array.isArray(res.data)) {
        const records = res.data
          .filter((r) => (r.eventid?._id === eventId || r.eventid === eventId))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        const record = records[0];
        if (record) {
          setPreConfirmationRecordId(record._id);
          const ids = (record.memberid || []).map((m) => m._id || m);
          setPreConfirmedIds(ids);
          return ids;
        }
      }
    } catch (err) {
      console.error("Error fetching pre-confirmation:", err);
    }
    setPreConfirmationRecordId(null);
    setPreConfirmedIds([]);
    return [];
  };


  // Main handler when event is selected
  const handleEventChange = async (eventId) => {
    setSelectedEvent(eventId);
    form.setFieldsValue({ eventId });

    // Reset first
    setSelectedMembers([]);
    setPreConfirmedIds([]);
    setPreConfirmationRecordId(null);
    form.setFieldsValue({ nonmember: [] });
    setCurrentRecordId(null);


    if (members.length === 0) {
      const interval = setInterval(async () => {
        if (members.length > 0) {
          clearInterval(interval);
          await loadDataForEvent(eventId);
        }
      }, 100);
    } else {
      await loadDataForEvent(eventId);
    }
  };

  // Load priority: Actual Attendance → Pre-Confirmation → Fresh
  const loadDataForEvent = async (eventId) => {
    const preIds = await fetchPreConfirmationData(eventId);
    const hasActual = await fetchEventAttendance(eventId);
    
    if (!hasActual && preIds.length > 0) {
      // If no attendance yet, default selected members to pre-confirmed ones
      setSelectedMembers(preIds);
    }
  };


  // Search filter
  useEffect(() => {
    const filtered = members.filter((m) =>
      m.Name.toLowerCase().includes(memberSearchText.toLowerCase())
    );
    setFilteredMembers(filtered);
    setSelectAll(
      filtered.length > 0 && filtered.every((m) => selectedMembers.includes(m._id))
    );
  }, [memberSearchText, members, selectedMembers]);

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    setSelectedMembers(checked ? filteredMembers.map((m) => m._id) : []);
  };

  const handleMemberSelect = (checked, memberId) => {
    const updated = checked
      ? [...selectedMembers, memberId]
      : selectedMembers.filter((id) => id !== memberId);
    setSelectedMembers(updated);
    setSelectAll(filteredMembers.length > 0 && updated.length === filteredMembers.length);
  };

  const handlePreConfirmSelect = (checked, memberId) => {
    const updated = checked
      ? [...preConfirmedIds, memberId]
      : preConfirmedIds.filter((id) => id !== memberId);
    setPreConfirmedIds(updated);
  };


  const handleVisitorSelect = (visitorId, index) => {
    const visitor = visitors.find(v => v._id === visitorId);
    if (visitor) {
      const currentNonMembers = form.getFieldValue("nonmember") || [];
      currentNonMembers[index] = {
        ...currentNonMembers[index],
        name: visitor.name,
        mobilenumber: visitor.phone,
        address: visitor.address
      };
      form.setFieldsValue({ nonmember: currentNonMembers });
    }
  };

  const handleSubmit = async (values) => {
    try {
      // 1. Prepare Payload for Attendance
      const attendancePayload = {
        eventid: values.eventId,
        memberid: selectedMembers,
        nonmember: values.nonmember || [],
      };

      // 2. Prepare Payload for Pre-Confirmation
      const preConfirmPayload = {
        eventid: values.eventId,
        memberid: preConfirmedIds,
        nonmember: values.nonmember || [], // Assuming guests should also be pre-confirmed if they're there
      };

      // 3. Save/Update Attendance
      if (currentRecordId) {
        await api.put(`/member/updateeventattendance/${currentRecordId}`, attendancePayload);
      } else {
        const res = await api.post("/member/eventattendance", attendancePayload);
        if (res.data?.data?._id) setCurrentRecordId(res.data.data._id);
      }

      // 4. Save/Update Pre-Confirmation
      if (preConfirmationRecordId) {
        await api.put(`/member/updateeventpreconfirm/${preConfirmationRecordId}`, preConfirmPayload);
      } else {
        const res = await api.post("/member/eventpreconfirm", preConfirmPayload);
        if (res.data?.data?._id) setPreConfirmationRecordId(res.data.data._id);
      }

      message.success("Attendance and Pre-Confirmation saved successfully");

      // Reload fresh data
      await fetchEventAttendance(values.eventId);
      await fetchPreConfirmationData(values.eventId);
    } catch (err) {
      console.error("Error saving records:", err);
      message.error(err.response?.data?.message || "Failed to save records.");
    }
  };


  return (
    <Row gutter={[24, 24]} style={{ padding: 16 }}>
      <Col span={24}>
        <Card
          title="Event Attendance Form"
          style={{ borderRadius: 8 }}
          extra={
            <Space>
              <Button type="primary" onClick={() => navigate("/dashboard/atten-list")}>
                View List
              </Button>
            </Space>
          }
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="eventId"
              label="Select Event"
              rules={[{ required: true, message: "Please select an event" }]}
            >
              <Select
                placeholder="Choose Event"
                showSearch
                optionFilterProp="children"
                onChange={handleEventChange}
                value={selectedEvent}
              >
                {events.map((event) => (
                  <Option key={event._id} value={event._id}>
                    {event.eventName}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {selectedEvent && (
              <>
                <Form.Item label="Member Attendance Status">
                  <div style={{ 
                    border: "1px solid #d9d9d9", 
                    borderRadius: 8, 
                    padding: 16,
                    backgroundColor: "#fff"
                  }}>
                    <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
                      <Col span={10}>
                        <Input
                          placeholder="Search members by name..."
                          prefix={<SearchOutlined />}
                          value={memberSearchText}
                          onChange={(e) => setMemberSearchText(e.target.value)}
                        />
                      </Col>
                      <Col span={14}>
                         <Checkbox checked={selectAll} onChange={handleSelectAll}>
                            Select All Visible (Attendance)
                          </Checkbox>
                      </Col>
                    </Row>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr',
                      padding: '10px',
                      backgroundColor: '#f0f2f5',
                      fontWeight: 'bold',
                      borderRadius: '4px 4px 0 0',
                      borderBottom: '2px solid #d9d9d9'
                    }}>
                      <div>Member Name</div>
                      <div style={{ textAlign: 'center' }}>Pre-confirmed</div>
                      <div style={{ textAlign: 'center' }}>Attendace</div>
                    </div>

                    <div style={{
                      height: 400,
                      overflowY: "auto",
                      border: "1px solid #f0f0f0",
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px'
                    }}>
                      {filteredMembers.length === 0 ? (
                        <div style={{ textAlign: "center", color: "#999", padding: 50 }}>
                          No members found
                        </div>
                      ) : (
                        filteredMembers.map((member) => {
                          const isPre = preConfirmedIds.includes(member._id);
                          const isAtt = selectedMembers.includes(member._id);
                          return (
                            <div
                              key={member._id}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 1fr 1fr',
                                padding: '12px 10px',
                                borderBottom: "1px solid #f0f0f0",
                                alignItems: 'center',
                                backgroundColor: isAtt ? '#e6f7ff' : '#fff'
                              }}
                            >
                              <span style={{ fontSize: 15, fontWeight: isAtt ? 'bold' : 'normal' }}>
                                {member.Name}
                              </span>
                              <div style={{ textAlign: 'center' }}>
                                <Checkbox 
                                  checked={isPre} 
                                  onChange={(e) => handlePreConfirmSelect(e.target.checked, member._id)}
                                />
                              </div>

                              <div style={{ textAlign: 'center' }}>
                                <Checkbox
                                  checked={isAtt}
                                  onChange={(e) => handleMemberSelect(e.target.checked, member._id)}
                                />
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    
                    <div style={{ marginTop: 16 }}>
                      <Text type="secondary">
                        Showing {filteredMembers.length} members. 
                        {selectedMembers.length} selected for attendance.
                      </Text>
                    </div>
                  </div>
                </Form.Item>


                <Form.Item label="Non-Members (Guests)">
                  <Form.List name="nonmember">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...rest }) => (
                          <Space key={key} align="baseline" style={{ display: "flex", marginBottom: 8, flexWrap: 'wrap' }}>
                             <Form.Item
                              {...rest}
                              label="Search Visitor"
                              style={{ minWidth: '200px' }}
                            >
                              <Select
                                placeholder="Select existing visitor"
                                showSearch
                                optionFilterProp="children"
                                onChange={(val) => handleVisitorSelect(val, name)}
                              >
                                {visitors.map(v => (
                                  <Option key={v._id} value={v._id}>{v.name} ({v.phone})</Option>
                                ))}
                              </Select>
                            </Form.Item>
                            <Form.Item
                              {...rest}
                              name={[name, "name"]}
                              label="Name"
                              rules={[{ required: true, message: "Name required" }]}
                            >
                              <Input placeholder="Name" />
                            </Form.Item>
                            <Form.Item
                              {...rest}
                              name={[name, "mobilenumber"]}
                              label="Mobile"
                              rules={[
                                { required: true, message: "Mobile required" },
                                { pattern: /^\d{10}$/, message: "Must be 10 digits" },
                              ]}
                            >
                              <Input placeholder="Mobile" />
                            </Form.Item>
                            <Form.Item
                              {...rest}
                              name={[name, "address"]}
                              label="Address"
                              rules={[{ required: true, message: "Address required" }]}
                            >
                              <Input placeholder="Address" />
                            </Form.Item>
                            <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red', marginTop: '35px' }} />
                          </Space>
                        ))}

                        <Form.Item>
                          <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                            Add Guest
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </Form.Item>
              </>
            )}

            <Form.Item>
              <Button type="primary" htmlType="submit" size="large">
                {currentRecordId ? "Update Attendance" : "Save Attendance"}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </Row>
  );
};

export default Attendance;